/**
 * ORA - AI Leadership Agent
 * Copyright (c) 2024-2026 s2cpaul
 * Licensed under MIT License
 * Repository: https://github.com/s2cpaul/ora-agent
 */

import { X, Crown, Brain, Sparkles, Calendar, FileText, Settings, LogOut, CheckCircle2, Zap, Edit3, Eye } from 'lucide-react';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuth } from '@/contexts/AuthContext';
import { useState, useEffect } from 'react';
import { addOrderToHistory } from '@/app/utils/orderHistory';

interface UserPortalProps {
  onClose: () => void;
  onOpenAgent?: () => void;
  onOpenSettings?: () => void;
  onOpenTraining?: () => void;
  onOpenOrderHistory?: () => void;
  adminEmail?: string; // Allow passing admin email from OrderHistoryPage
}

export function UserPortal({ 
  onClose, 
  onOpenAgent,
  onOpenSettings,
  onOpenTraining,
  onOpenOrderHistory,
  adminEmail
}: UserPortalProps) {
  const { user, logout } = useAuth();
  const { subscription, tier, loading } = useSubscription();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [orders, setOrders] = useState<any[]>([]);
  
  // Script submission form state
  const [showScriptForm, setShowScriptForm] = useState(false);
  const [scriptText, setScriptText] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if current user is admin (case-insensitive and trim whitespace)
  const normalizedEmail = user?.email?.toLowerCase().trim();
  const isAdmin = 
    normalizedEmail === 'carapaulson1@gmail.com' || 
    normalizedEmail === 'cara@oratf.info' || 
    normalizedEmail === adminEmail?.toLowerCase().trim();
  
  // Debug logging
  console.log('UserPortal - User Email:', user?.email);
  console.log('UserPortal - Normalized Email:', normalizedEmail);
  console.log('UserPortal - Is Admin:', isAdmin);

  useEffect(() => {
    if (isAdmin) {
      // Load orders from localStorage for admin view
      const stored = localStorage.getItem('orderHistory');
      if (stored) {
        const orderData = JSON.parse(stored);
        setOrders(orderData.reverse()); // Show newest first
      }
    }
  }, [isAdmin]);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      onClose();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8">
          <p className="text-gray-700">Loading your portal...</p>
        </div>
      </div>
    );
  }

  // Get tier display info
  const getTierInfo = () => {
    switch (tier) {
      case 'free':
        return { name: 'Free', icon: Sparkles, color: 'gray', bgColor: 'bg-gray-700', textColor: 'text-white' };
      case 'solo':
        return { name: 'Solo Pro', icon: Crown, color: 'blue', bgColor: 'bg-blue-100', textColor: 'text-blue-700' };
      case 'avatar':
        return { name: 'AI Persona Agent', icon: Brain, color: 'purple', bgColor: 'bg-purple-100', textColor: 'text-purple-700' };
      case 'buddy':
        return { name: 'AI Pro Level 5 (Buddy)', icon: Crown, color: 'yellow', bgColor: 'bg-yellow-100', textColor: 'text-yellow-700' };
      case 'pro':
        return { name: 'Pro', icon: Zap, color: 'teal', bgColor: 'bg-teal-100', textColor: 'text-teal-700' };
      case 'team':
        return { name: 'Team', icon: Brain, color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-700' };
      case 'enterprise':
        return { name: 'Enterprise', icon: Brain, color: 'indigo', bgColor: 'bg-indigo-100', textColor: 'text-indigo-700' };
      default:
        return { name: 'Free', icon: Sparkles, color: 'gray', bgColor: 'bg-gray-700', textColor: 'text-white' };
    }
  };

  const tierInfo = getTierInfo();
  const TierIcon = tierInfo.icon;

  // Helper functions for formatting
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  
  // Calculate word count
  const wordCount = scriptText.trim().split(/\s+/).filter(w => w).length;
  
  // Handle script submission
  const handleSubmitScript = async () => {
    if (wordCount === 0) {
      alert('Please enter a script before submitting.');
      return;
    }
    
    if (!user?.email) {
      alert('User email not found. Please log in again.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Simulate processing delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Generate transaction ID
      const transactionId = `TXN-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
      const currentTimestamp = Date.now();
      
      // Add order to history with script metadata
      addOrderToHistory({
        userId: `USER-${Date.now()}`,
        userEmail: user.email,
        orderType: 'alacarte',
        productName: 'Script Submission',
        amount: 0, // Free for subscribers
        status: 'pending',
        paymentMethod: 'Subscription',
        transactionId: transactionId,
        customerId: user.email,
        scripts: [
          {
            content: scriptText,
            submittedBy: user.email,
            submittedAt: currentTimestamp,
            wordCount: wordCount
          }
        ],
      });
      
      // Success feedback
      alert(`✅ Script submitted successfully!\\n\\nYour script (${wordCount} words) has been received.\\n\\nTransaction ID: ${transactionId}\\n\\nOur team will process your request shortly.`);
      
      // Reset form
      setScriptText('');
      setShowPreview(false);
      setShowScriptForm(false);
    } catch (error: any) {
      alert(`Submission failed: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Admin view - show order history
  if (isAdmin) {
    return (
      <div className="fixed inset-0 bg-gray-200/95 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-4xl w-full shadow-2xl border border-gray-300 max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-teal-600 p-6 rounded-t-2xl relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
              aria-label="Close portal"
            >
              <X className="size-5" />
            </button>
            
            <div className="text-center">
              <h1 className="text-2xl font-bold text-white mb-1">
                Admin Portal - Order History
              </h1>
              <p className="text-green-100 text-sm">{user?.email}</p>
            </div>
          </div>

          {/* Orders List */}
          <div className="flex-1 overflow-y-auto p-6">
            {orders.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No orders found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div
                    key={order.id}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    {/* Order Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <h3 className="font-bold text-lg text-black">{order.id}</h3>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            order.status === 'delivered'
                              ? 'bg-green-100 text-green-700'
                              : order.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {order.status.toUpperCase()}
                        </span>
                      </div>
                      <span className="text-sm text-gray-600">{formatDate(order.timestamp)}</span>
                    </div>

                    {/* Customer Info */}
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <p className="text-xs text-gray-500">Customer Email</p>
                        <p className="text-sm font-medium text-black">{order.userEmail}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Customer ID</p>
                        <p className="text-sm font-medium text-black font-mono">{order.customerId || order.userId}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Product</p>
                        <p className="text-sm font-medium text-black">{order.planName || order.productName || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Amount</p>
                        <p className="text-sm font-medium text-green-600">{formatCurrency(order.amount)}</p>
                      </div>
                    </div>

                    {/* Script Section */}
                    {order.script && (
                      <div className="mb-3 pt-3 border-t border-gray-200">
                        <p className="text-xs text-gray-500 mb-2 font-semibold">Script</p>
                        <div className="bg-gray-50 p-3 rounded border border-gray-200">
                          <p className="text-sm text-black whitespace-pre-wrap">{order.script}</p>
                        </div>
                      </div>
                    )}

                    {/* Media Links */}
                    {(order.mediaUrl || order.mediaType) && (
                      <div className="pt-3 border-t border-gray-200">
                        <div className="grid grid-cols-2 gap-4">
                          {order.mediaType && (
                            <div>
                              <p className="text-xs text-gray-500 mb-1 font-semibold">Media Type</p>
                              <p className="text-sm font-medium text-black capitalize">{order.mediaType}</p>
                            </div>
                          )}
                          {order.mediaUrl && (
                            <div>
                              <p className="text-xs text-gray-500 mb-1 font-semibold">Media File</p>
                              <a
                                href={order.mediaUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:text-blue-800 underline break-all"
                              >
                                View {order.mediaType === 'voice' ? 'Voice Recording' : order.mediaType === 'photo' ? 'Image' : 'File'}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 p-4 bg-gray-50 rounded-b-2xl">
            <p className="text-xs text-gray-600 text-center">
              Showing {orders.length} order{orders.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Regular user view
  return (
    <div className="fixed inset-0 bg-gray-200/95 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-gray-300 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 rounded-t-2xl relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
            aria-label="Close portal"
          >
            <X className="size-5" />
          </button>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-white rounded-full mx-auto mb-3 flex items-center justify-center">
              <span className="text-3xl">👤</span>
            </div>
            <h1 className="text-xl font-bold text-white mb-1">
              {user?.displayName || user?.email || 'Welcome'}
            </h1>
            <p className="text-blue-100 text-sm">{user?.email}</p>
          </div>
        </div>

        {/* Subscription Status */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-600">Current Plan</h2>
            <div className={`flex items-center gap-2 ${tierInfo.bgColor} ${tierInfo.textColor} px-3 py-1 rounded-full`}>
              <TierIcon className="size-4" />
              <span className="text-sm font-bold">{tierInfo.name}</span>
            </div>
          </div>
          
          {subscription && (
            <div className="space-y-2">
              {tier !== 'free' && (
                <>
                  <div className="flex items-center gap-2 text-sm text-black">
                    <CheckCircle2 className="size-4 text-green-600" />
                    <span>Active Subscription</span>
                  </div>
                  {subscription.questionsPerMonth === -1 ? (
                    <div className="flex items-center gap-2 text-sm text-black">
                      <CheckCircle2 className="size-4 text-green-600" />
                      <span>Unlimited Questions</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-black">
                      <CheckCircle2 className="size-4 text-green-600" />
                      <span>{subscription.questionsRemaining} / {subscription.questionsPerMonth} Questions Remaining</span>
                    </div>
                  )}
                  {subscription.canUploadDocuments && (
                    <div className="flex items-center gap-2 text-sm text-black">
                      <CheckCircle2 className="size-4 text-green-600" />
                      <span>Document Upload Enabled</span>
                    </div>
                  )}
                  {subscription.canIntegrateGoogleDrive && (
                    <div className="flex items-center gap-2 text-sm text-black">
                      <CheckCircle2 className="size-4 text-green-600" />
                      <span>Google Drive Integration</span>
                    </div>
                  )}
                </>
              )}
              {tier === 'free' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                  <p className="text-xs text-black mb-2">
                    Upgrade to unlock premium features!
                  </p>
                  <ul className="space-y-1 text-xs text-gray-700">
                    <li className="flex items-start gap-1.5">
                      <CheckCircle2 className="size-3 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>Unlimited questions</span>
                    </li>
                    <li className="flex items-start gap-1.5">
                      <CheckCircle2 className="size-3 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>Custom AI avatar</span>
                    </li>
                    <li className="flex items-start gap-1.5">
                      <CheckCircle2 className="size-3 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>Document integration</span>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="p-6">
          <h2 className="text-sm font-semibold text-gray-600 mb-3">Quick Actions</h2>
          <div className="space-y-2">
            {onOpenAgent && (
              <button
                onClick={() => {
                  onOpenAgent();
                  onClose();
                }}
                className="w-full flex items-center gap-3 p-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg transition-all group"
              >
                <Brain className="size-5 fill-yellow-400" />
                <div className="flex-1 text-left">
                  <div className="font-semibold text-sm">Open AI Agent</div>
                  <div className="text-xs text-blue-100">Start asking questions</div>
                </div>
              </button>
            )}
            
            {onOpenTraining && (
              <button
                onClick={() => {
                  onOpenTraining();
                  onClose();
                }}
                className="w-full flex items-center gap-3 p-3 bg-white hover:bg-gray-50 border-2 border-gray-200 rounded-lg transition-all group"
              >
                <FileText className="size-5 text-black" />
                <div className="flex-1 text-left">
                  <div className="font-semibold text-sm text-black">Training Library</div>
                  <div className="text-xs text-gray-600">Access learning content</div>
                </div>
              </button>
            )}
            
            {onOpenSettings && (
              <button
                onClick={() => {
                  onOpenSettings();
                  onClose();
                }}
                className="w-full flex items-center gap-3 p-3 bg-white hover:bg-gray-50 border-2 border-gray-200 rounded-lg transition-all group"
              >
                <Settings className="size-5 text-black" />
                <div className="flex-1 text-left">
                  <div className="font-semibold text-sm text-black">Settings</div>
                  <div className="text-xs text-gray-600">Manage your account</div>
                </div>
              </button>
            )}
            
            {onOpenOrderHistory && tier !== 'free' && (
              <button
                onClick={() => {
                  onOpenOrderHistory();
                  onClose();
                }}
                className="w-full flex items-center gap-3 p-3 bg-white hover:bg-gray-50 border-2 border-gray-200 rounded-lg transition-all group"
              >
                <Calendar className="size-5 text-black" />
                <div className="flex-1 text-left">
                  <div className="font-semibold text-sm text-black">Order History</div>
                  <div className="text-xs text-gray-600">View past purchases</div>
                </div>
              </button>
            )}
            
            {/* Order with New Script */}
            {tier !== 'free' && (
              <button
                onClick={() => setShowScriptForm(true)}
                className="w-full flex items-center gap-3 p-3 bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white rounded-lg transition-all group"
              >
                <Edit3 className="size-5" />
                <div className="flex-1 text-left">
                  <div className="font-semibold text-sm">Order with New Script</div>
                  <div className="text-xs text-green-100">Submit a new script for processing</div>
                </div>
              </button>
            )}
          </div>
        </div>

        {/* Logout */}
        <div className="p-6 pt-0">
          <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="w-full flex items-center justify-center gap-2 p-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <LogOut className="size-5" />
            <span className="font-semibold text-sm">
              {isLoggingOut ? 'Logging out...' : 'Log Out'}
            </span>
          </button>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50 rounded-b-2xl">
          <p className="text-xs text-gray-600 text-center">
            ORA - Your AI Leadership Assistant
          </p>
          <p className="text-xs text-gray-500 text-center mt-1">
            © 2024-2026 All rights reserved
          </p>
        </div>
      </div>
      
      {/* Script Submission Form Modal */}
      {showScriptForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl border border-gray-300 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-600 to-teal-600 p-4 rounded-t-xl relative">
              <button
                onClick={() => {
                  setShowScriptForm(false);
                  setShowPreview(false);
                  setScriptText('');
                }}
                className="absolute top-2 right-2 text-white hover:bg-white/20 p-1.5 rounded-lg transition-colors"
                aria-label="Close form"
              >
                <X className="size-4" />
              </button>
              <h2 className="text-xl font-bold text-white text-center">Submit New Script</h2>
              <p className="text-green-100 text-sm text-center mt-1">Free for subscribers</p>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              {!showPreview ? (
                <>
                  {/* Script Input */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Your Script
                    </label>
                    <textarea
                      value={scriptText}
                      onChange={(e) => setScriptText(e.target.value)}
                      placeholder="Enter your script here..."
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none text-black resize-none h-64"
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Word Counter */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <span className="text-sm text-gray-600">Word Count:</span>
                    <span className={`text-lg font-bold ${wordCount > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                      {wordCount}
                    </span>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowPreview(true)}
                      disabled={wordCount === 0 || isSubmitting}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Eye className="size-4" />
                      Preview
                    </button>
                    <button
                      onClick={() => {
                        setShowScriptForm(false);
                        setShowPreview(false);
                        setScriptText('');
                      }}
                      disabled={isSubmitting}
                      className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Preview Mode */}
                  <div>
                    <h3 className="text-lg font-bold text-black mb-3">Preview</h3>
                    <div className="bg-gray-50 border-2 border-green-200 rounded-lg p-4 max-h-64 overflow-y-auto">
                      <p className="text-sm text-black whitespace-pre-wrap">{scriptText}</p>
                    </div>
                  </div>

                  {/* Preview Stats */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-xs text-green-700 font-semibold">Word Count</p>
                      <p className="text-2xl font-bold text-green-600">{wordCount}</p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-xs text-blue-700 font-semibold">Submitting As</p>
                      <p className="text-sm font-bold text-blue-600 truncate">{user?.email}</p>
                    </div>
                  </div>

                  {/* Preview Action Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowPreview(false)}
                      disabled={isSubmitting}
                      className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Edit
                    </button>
                    <button
                      onClick={handleSubmitScript}
                      disabled={isSubmitting}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="size-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="size-4" />
                          Submit Order
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}